type Intent = { mode: 'login' | 'signup'; href: string };
type Pending = { state: string; createdAt: number; clientId: string; redirectUri: string; intent: Intent };
const pendingKey = 'pte_home_apple_redirect';
const maxAgeMs = 10 * 60 * 1000;
const retryMessage = 'Apple sign in could not be verified. Please try again.';

export function startAppleRedirect(clientId: string, redirectUri: string, intent: Intent) {
  const returnUrl = new URL(window.location.href);
  returnUrl.hash = '';
  // This marker lets the homepage consume only its own Apple callbacks.
  returnUrl.searchParams.set('apple_callback', '1');
  const state = btoa(JSON.stringify({ nonce: crypto.randomUUID(), returnUrl: returnUrl.href }))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  const pending: Pending = { state, createdAt: Date.now(), clientId, redirectUri, intent };
  // Fail before leaving the page if Safari cannot retain the state for verification.
  window.sessionStorage.setItem(pendingKey, JSON.stringify(pending));
  const authorize = new URL('https://appleid.apple.com/auth/authorize');
  authorize.search = new URLSearchParams({ client_id: clientId, redirect_uri: redirectUri,
    response_type: 'code', scope: 'name email', response_mode: 'form_post', state }).toString();
  window.location.assign(authorize.href);
}

export function consumeAppleRedirect(appBaseUrl: string) {
  const callbackWindow = window as Window & { __PTE_APPLE_CALLBACK__?: string };
  const url = new URL(callbackWindow.__PTE_APPLE_CALLBACK__ ?? window.location.href);
  delete callbackWindow.__PTE_APPLE_CALLBACK__;
  if (url.searchParams.get('apple_callback') !== '1') return null;
  const params = new URLSearchParams(url.search);
  for (const key of ['apple_callback', 'state', 'code', 'id_token', 'user', 'error', 'error_description']) url.searchParams.delete(key);
  window.history.replaceState(window.history.state, '', url.href);
  const fallback: Intent = { mode: 'login', href: new URL('/', appBaseUrl).href };
  try {
    const stored = window.sessionStorage.getItem(pendingKey);
    window.sessionStorage.removeItem(pendingKey);
    const pending: Pending | null = stored ? JSON.parse(stored) : null;
    if (!pending || pending.state !== params.get('state') || !Number.isFinite(pending.createdAt)
      || Date.now() - pending.createdAt < 0 || Date.now() - pending.createdAt > maxAgeMs
      || !pending.clientId || !pending.redirectUri || !['login', 'signup'].includes(pending.intent.mode)
      || new URL(pending.intent.href).origin !== new URL(appBaseUrl).origin) {
      return { intent: fallback, error: retryMessage };
    }
    if (params.has('error') || !params.get('code')) {
      return { intent: pending.intent, error: params.get('error') === 'user_cancelled_authorize'
        ? 'Apple sign in was cancelled. Please try again.' : retryMessage };
    }
    const user = JSON.parse(params.get('user') || '{}');
    const text = (value: unknown) => typeof value === 'string' ? value : undefined;
    return { intent: pending.intent, payload: {
      authorizationCode: params.get('code')!, clientId: pending.clientId, redirectUri: pending.redirectUri,
      email: text(user?.email), firstName: text(user?.name?.firstName), lastName: text(user?.name?.lastName),
    } };
  } catch {
    return { intent: fallback, error: retryMessage };
  }
}
